import { Button, Input, Select } from "../../custom";
import { ReactComponent as Upload } from "../../assets/upload.svg";
import styles from "./styles.module.scss";
import { Form, Formik, FormikValues } from "formik";
import React, { FC, useEffect, useRef, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  createUpdatePersonalInfo,
  getAllDisability,
  getAllGender,
  getAllMaritalStatus,
  getAllReligion,
  getAllTitle,
  getCountries,
  getLgaByStateId,
  getPersonalInfoByApplicantId,
  getStateByCountryId,
} from "../../utils/request";
import { useAtom, useAtomValue } from "jotai";
import { pageIndexAtom, userAtom } from "../../utils/store";
import { App, Spin } from "antd";
import { formatDate } from "../../utils/formatDate";
import { object } from "yup";
import { validator } from "../../utils/validator";
import { useFileValidator } from "../../hooks/useFileValidator";

interface ComponentProps {
  isEdit?: boolean;
  onClose: () => void;
}

interface PersonalInfoInit {
  firstName: string;
  middleName: string;
  lastName: string;
  gender: string;
  maritalStatus: string;
  religion: string;
  phoneNumber: string;
  dob: string;
  stateOfOrigin: string;
  email: string;
  address: string;
  country: string;
  city: string;
  zipCode: string;
  disability: string;
  description: string;
  lga: string;
  title: string;
}

const PersonalInfo: FC<ComponentProps> = ({ isEdit, onClose }) => {
  const { validateFile } = useFileValidator();
  const [countryId, setCountryId] = useState(0);
  const [stateId, setStateId] = useState(0);
  const [shouldContinue, setShouldContinue] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const user = useAtomValue(userAtom);
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);

  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const queries = useQueries({
    queries: [
      { queryKey: ["get-countries"], queryFn: getCountries },
      {
        queryKey: ["get-state-id", countryId],
        queryFn: () => getStateByCountryId(countryId),
        enabled: !!countryId,
        refetchOnWindowFocus: true,
      },
      { queryKey: ["get-marital-status"], queryFn: getAllMaritalStatus },

      { queryKey: ["get-disability"], queryFn: getAllDisability },
      { queryKey: ["get-religion"], queryFn: getAllReligion },
      {
        queryKey: ["get-lga-id", stateId],
        queryFn: () => getLgaByStateId(stateId),
        enabled: !!stateId,
        refetchOnWindowFocus: true,
      },
      {
        queryKey: ["get-personal-info-id", user?.applicantId],
        queryFn: () => getPersonalInfoByApplicantId(user?.applicantId ?? ""),
        enabled: !!user?.applicantId,
      },
      { queryKey: ["get-all-gender"], queryFn: getAllGender },
      { queryKey: ["get-all-title"], queryFn: getAllTitle },
    ],
  });

  const countriesQuery = queries[0];
  const stateQuery = queries[1];
  const maritalStatusQuery = queries[2];
  const disabilityQuery = queries[3];
  const religionQuery = queries[4];
  const LGAQuery = queries[5];
  const personalInfoQuery = queries[6];
  const genderQuery = queries[7];
  const titleQuery = queries[8];

  const {
    data: personalInfo,
    isLoading: isPersonalInfoLoading,
    isError: isPersonalInfoError,
    error: personalInfoError,
  } = personalInfoQuery;

  const { data: title, isLoading: isTitleLoading } = titleQuery;

  const countryData = countriesQuery?.data?.data ?? [];
  const stateData = stateQuery?.data?.data ?? [];
  const maritalStatusData = maritalStatusQuery?.data?.data ?? [];
  const disabilityData = disabilityQuery?.data?.data ?? [];
  const religionData = religionQuery?.data?.data ?? [];
  const lgaData = LGAQuery?.data?.data ?? [];
  const personalInfoData = personalInfo?.data ?? ({} as PersonalInfo);
  const genderData = genderQuery?.data?.data ?? [];
  const titleData = title?.data ?? [];

  const isLgaLoading = LGAQuery?.isLoading;

  const createUpdatePersonalInfoMutation = useMutation({
    mutationFn: createUpdatePersonalInfo,
  });

  const handlePersonalInfo = async (values: FormikValues) => {
    const formData = new FormData();

    formData.append("Id", personalInfoData?.id?.toString() ?? "0");
    formData.append("ApplicantId", user?.applicantId ?? "");
    formData.append("FName", values.firstName);
    formData.append("MNane", values.middleName);
    formData.append("LName", values.lastName);
    formData.append("GenderId", values.gender);
    formData.append("MaritalStatusId", values.maritalStatus);
    formData.append("ReligionId", values.religion);
    formData.append("PhoneNo", values.phoneNumber);
    formData.append("DateOfBirth", values.dob);
    formData.append("CountryId", values.country);
    formData.append("LGAId", values.lga);
    formData.append("StateOfOriginId", values.stateOfOrigin);
    formData.append("Address", values.address);
    formData.append("Email", values.email);
    formData.append("ZipCode", values.zipCode);
    formData.append("DisabilityId", values.disability);
    formData.append("DescribeDisability", values.description);
    formData.append("IsActive", String(true));
    formData.append("TitleId", values.title);
    formData.append("City", values.city);
    if (uploadedImage) {
      formData.append("Image", uploadedImage ?? personalInfoData?.imageUrl);
    }

    try {
      await createUpdatePersonalInfoMutation?.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          queryClient.refetchQueries({ queryKey: ["get-personal-info-id"] });

          // if (user.programType?.toLowerCase()?.includes("msc")) {
          //   setPageIndex("3");
          // } else {
          //   setPageIndex("2");
          // }

          onClose();
          if (shouldContinue) {
            handleNextNavigation();
          }
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const { isValidType, isValidSize } = validateFile(file, {
        allowedTypes: ["image/jpeg", "image/png", "image/jpg"],
        maxSizeMB: 0.24,
      });
      if (isValidType && isValidSize) {
        setUploadedImage(file);
      } else {
        notification.error({
          message: "Invalid file",
          description:
            "Please upload a valid image file (JPEG, PNG, JPG) under 240 KB.",
        });
      }
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const countryOptions = countryData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.countryName}
    </option>
  ));

  const stateOptions = stateData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.stateName}
    </option>
  ));

  const lgaOptions = lgaData?.map((item) => {
    if (isLgaLoading) {
      return <option value="">{<Spin size="small" />}</option>;
    } else {
      return (
        <option key={item?.id} value={item?.id}>
          {item?.lgaName}
        </option>
      );
    }
  });

  const titleOptions = titleData?.map((item) => {
    if (isTitleLoading) {
      return <option value="">{<Spin size="small" />}</option>;
    } else {
      return (
        <option key={item?.id} value={item?.id}>
          {item?.titleName}
        </option>
      );
    }
  });

  const maritalStatusOptions = maritalStatusData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.statusName}
    </option>
  ));

  const disabilityOptions = disabilityData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  const religionOptions = religionData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  const genderOptions = genderData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.genderName}
    </option>
  ));

  const validationSchema = object().shape({
    firstName: validator.firstName,
    middleName: validator.middleName,
    lastName: validator.lastName,
    gender: validator.gender,
    maritalStatus: validator.maritalStatus,
    religion: validator.religion,
    phoneNumber: validator.phoneNumber,
    dob: validator.dob,
    stateOfOrigin: validator.stateOfOrigin,
    email: validator.email,
    address: validator.address,
    country: validator.country,
    city: validator.city,
    zipCode: validator.zipCode,
    disability: validator.disability,
    title: validator.title,
    lga: validator.lga,
  });

  const handleNextNavigation = () => {
    setPageIndex(pageIndex < 5 ? pageIndex + 1 : pageIndex);
  };

  useEffect(() => {
    setCountryId(personalInfoData?.countryId ?? 0);
  }, [personalInfoData?.countryId]);

  useEffect(() => {
    setStateId(personalInfoData?.stateOfOriginId ?? 0);
  }, [personalInfoData?.stateOfOriginId]);

  if (isPersonalInfoLoading) {
    return (
      <div className={styles.loader}>
        <Spin size="large" />
      </div>
    );
  }

  if (isPersonalInfoError) {
    return <div>{personalInfoError?.message}</div>;
  }

  return (
    <>
      <section className={styles.section}>
        <h2>Personal Information</h2>
        <p>All fields marked with a (*) are required.</p>
      </section>

      <hr />

      <div className={styles.section}>
        <section className={styles.upload}>
          <div>
            <p>Upload your passport photograph</p>
            <h5>
              <b>Requirements:</b>
            </h5>

            <ul>
              <li>Picture must be clear</li>
              <li>Must be on a white background</li>
              <li>Max photo size 240kb</li>
              <li>Must not be older than 6 months</li>
            </ul>
          </div>

          <div className={styles.uploadCta} onClick={triggerFileUpload}>
            {uploadedImage ? (
              <img
                src={URL.createObjectURL(uploadedImage)}
                alt="Uploaded"
                width="100"
                height="100"
              />
            ) : personalInfoData?.imageUrl ? (
              <img
                src={personalInfoData?.imageUrl}
                alt="Uploaded"
                width="100"
                height="100"
              />
            ) : (
              <Upload />
            )}

            <div>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={handleImageUpload}
              />
              <button>
                {uploadedImage ? "Change Passport" : "Upload Passport"}
              </button>
            </div>
          </div>
        </section>
        <br />

        <Formik<PersonalInfoInit>
          initialValues={{
            firstName: personalInfoData?.fName ?? user?.firstName,
            middleName: personalInfoData?.mNane ?? "",
            lastName: personalInfoData?.lName ?? user?.lastName,
            gender: personalInfoData?.genderId?.toString() ?? "",
            maritalStatus: personalInfoData?.maritalStatusId?.toString() ?? "",
            religion: personalInfoData?.religionId?.toString() ?? "",
            phoneNumber: personalInfoData?.phoneNo ?? user?.phoneNo,
            dob: formatDate(personalInfoData?.dateOfBirth) ?? "",
            stateOfOrigin: personalInfoData?.stateOfOriginId?.toString() ?? "",
            email: personalInfoData?.email ?? user?.email,
            address: personalInfoData?.address ?? "",
            country: personalInfoData?.countryId?.toString() ?? "",
            zipCode: personalInfoData?.zipCode?.toString() ?? "",
            disability: personalInfoData?.disabilityId?.toString() ?? "",
            description: personalInfoData?.describeDisability ?? "",
            lga: personalInfoData?.lgaId?.toString() ?? "",
            title: personalInfoData?.titleId?.toString() ?? "",
            city: personalInfoData?.city ?? "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            // check if image has been uploaded
            if (uploadedImage || personalInfoData?.imageUrl) {
              handlePersonalInfo(values);
            } else {
              notification.error({
                message: "Passport photograph required",
                description:
                  "Please upload your passport photograph to proceed.",
              });
            }
          }}
        >
          {({ handleChange, setFieldValue, values, submitForm }) => {
            return (
              <Form>
                <section className={styles.grid}>
                  <Select
                    name="title"
                    label="Title"
                    placeholder="Title"
                    options={titleOptions}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("title", e.target.value);
                    }}
                    asterisk
                  />
                  <Input
                    type="text"
                    name="firstName"
                    label="First Name"
                    placeholder="First name"
                    asterisk
                  />
                  <Input
                    type="text"
                    name="middleName"
                    label="Middle Name"
                    placeholder="Middle name"
                  />
                  <Input
                    type="text"
                    name="lastName"
                    label="Last Name"
                    placeholder="Last name"
                    asterisk
                  />
                  <Select
                    name="gender"
                    label="Gender"
                    placeholder="Gender"
                    options={genderOptions}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("gender", e.target.value);
                    }}
                    asterisk
                  />
                  <Select
                    name="maritalStatus"
                    label="Marital Status"
                    placeholder="Marital Status"
                    options={maritalStatusOptions}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("maritalStatus", e.target.value);
                    }}
                    asterisk
                  />
                  <Select
                    name="religion"
                    label="Religion"
                    placeholder="Religion"
                    options={religionOptions}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("religion", e.target.value);
                    }}
                    asterisk
                  />

                  <Input
                    type="date"
                    name="dob"
                    label="Date of Birth"
                    placeholder="Date of Birth"
                    asterisk
                  />
                  <Select
                    name="country"
                    label="Country"
                    placeholder="Enter country"
                    options={countryOptions}
                    onChange={(e) => {
                      handleChange(e);
                      setCountryId(parseInt(e.target.value));
                      setFieldValue("country", e.target.value);
                    }}
                    asterisk
                  />
                  <Select
                    name="stateOfOrigin"
                    label="State Of Origin"
                    placeholder="State Of Origin"
                    options={stateOptions}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("stateOfOrigin", e.target.value);
                      setStateId(parseInt(e.target.value));
                    }}
                    asterisk
                  />

                  <Select
                    name="lga"
                    label="LGA"
                    placeholder="LGA"
                    options={lgaOptions}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("lga", e.target.value);
                    }}
                    asterisk
                  />
                  <Input
                    name="email"
                    label="Email"
                    placeholder="Enter email"
                    asterisk
                  />
                </section>

                <div className={styles.noGrid}>
                  <Input
                    type="textarea"
                    name="address"
                    label="Address"
                    placeholder="Enter address"
                    asterisk
                  />
                </div>

                <section className={styles.grid}>
                  <Input
                    name="phoneNumber"
                    label="Phone Number"
                    placeholder="Phone Number"
                    asterisk
                  />

                  <Input name="city" label="City" placeholder="City" asterisk />

                  <Input
                    name="zipCode"
                    label="Zip Code"
                    placeholder="Enter zip code"
                  />

                  <Select
                    name="disability"
                    label="Disability"
                    placeholder="Enter disability"
                    options={disabilityOptions}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("disability", e.target.value);
                      setStateId(parseInt(e.target.value));
                    }}
                    asterisk
                  />
                </section>

                {disabilityData?.find(
                  (item) => item?.id === Number(values.disability),
                )?.name !== "None" && (
                  <div className={styles.noGrid}>
                    <Input
                      type="textarea"
                      name="description"
                      label="Describe in details your disability"
                      placeholder="Type here..."
                    />
                  </div>
                )}

                <div className={styles.btnGroup}>
                  <Button
                    className={styles.saveBtn}
                    text="Save"
                    type="submit"
                    isLoading={createUpdatePersonalInfoMutation.isPending}
                  />
                  <Button
                    text="Save and Continue"
                    type="button"
                    onClick={() => {
                      setShouldContinue(true);
                      submitForm();
                    }}
                    isLoading={createUpdatePersonalInfoMutation.isPending}
                  />
                  {/* <OnboardingButtonGroup
                    isPrevButton={false}
                    backButtonText="Back"
                    onClick={handleBackNavigate}
                    onNextClick={() => {
                      setShouldContinue(true);
                      submitForm();
                    }}
                    isLoading={createUpdatePersonalInfoMutation.isPending}
                    isEdit={isEdit}
                  /> */}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default PersonalInfo;
